// Форма 20-ОПП — повідомлення про об'єкти оподаткування (J1312007).
// Подається протягом 10 робочих днів після реєстрації, зміни або закриття
// об'єкта (п. 8.4 Порядку обліку платників, наказ Мінфіну № 1588).
//
// Графи розділу 3 і відповідні теги XML:
//   2  T1RXXXXG2    код ознаки надання інформації (1 нове / 3 зміна / 6 закриття)
//   3  T1RXXXXG3    тип об'єкта за довідником ДПС (3 цифри, напр. 378 — офіс)
//   4  T1RXXXXG4S   найменування об'єкта
//   5  T1RXXXXG5    ідентифікатор: код типу + 5-значний внутрішній номер
//   6  T1RXXXXG6S   КАТОТТГ (4–5 рівень; для Києва — код району міста)
//   7  T1RXXXXG7S   область, район, населений пункт
//   8  T1RXXXXG8S   тип вулиці           11 T1RXXXXG11S  корпус
//   9  T1RXXXXG9S   назва вулиці         12 T1RXXXXG12S  номер приміщення
//  10  T1RXXXXG10S  номер будинку        13 T1RXXXXG13S  додаткові відомості
//  14  T1RXXXXG14   стан об'єкта         15 T1RXXXXG15   вид права
//  16  T1RXXXXG16   взяти на облік за неосновним місцем
//  17  T1RXXXXG17S  реєстраційний номер (кадастровий, номер кузова тощо)

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;")
  .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const ddmmyyyy = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}${p(d.getMonth() + 1)}${d.getFullYear()}`;
};

// Графа 2. Код «4» у довіднику станів відсутній — це не помилка.
export const INFO_SIGN = {
  1: "первинне надання інформації",
  3: "зміна відомостей (у т.ч. виправлення помилки)",
  6: "закриття об'єкта",
};

export const OBJ_STATE = {
  1: "будується / готується до введення в експлуатацію",
  2: "експлуатується",
  3: "тимчасово не експлуатується",
  5: "непридатний до експлуатації",
  6: "об'єкт відчужений / повернутий власнику",
  7: "зміна призначення / перепрофілювання",
  8: "орендується",
  9: "здається в оренду",
};

export const OBJ_RIGHT = {
  1: "право власності",
  2: "право володіння",
  3: "право користування (сервітут, емфітевзис, суперфіцій)",
  4: "право господарського відання",
  5: "право оперативного управління",
  6: "право постійного користування",
  7: "право довгострокового користування або оренди",
  8: "право короткострокового користування, оренди або найму",
  9: "іпотека",
  10: "довірче управління майном",
};

// Зворотне зіставлення: в обліковій картці стан і право приходять текстом
const byText = (dict) => (s) => {
  const t = String(s ?? "").toLowerCase().trim();
  if (!t) return null;
  for (const [code, name] of Object.entries(dict)) {
    const n = name.toLowerCase();
    if (n === t || n.startsWith(t) || t.startsWith(n.split(" (")[0])) return Number(code);
  }
  return null;
};
export const stateCode = byText(OBJ_STATE);
export const rightCode = byText(OBJ_RIGHT);

// Типи вулиць, які трапляються в адресах облікової картки
const STREET_TYPES = [
  ["вул", "вулиця"], ["просп", "проспект"], ["пров", "провулок"], ["бул", "бульвар"],
  ["пл", "площа"], ["ш", "шосе"], ["наб", "набережна"], ["туп", "тупик"],
  ["узв", "узвіз"], ["мкр", "мікрорайон"], ["с-ще", "селище"],
];

// Розбір адреси з облікової картки на графи 7–11.
// Приклад: «М. КИЇВ, ДЕСНЯНСЬКИЙ РАЙОН СКЛАД 124,9 КВ.М. ВУЛ. ПУХІВСЬКА, БУД. 1-A»
//   → { place: «М. КИЇВ, ДЕСНЯНСЬКИЙ РАЙОН», streetType: «вулиця»,
//       street: «ПУХІВСЬКА», house: «1», corp: «A» }
export function parseAddress(raw) {
  const s = String(raw ?? "").replace(/\s+/g, " ").trim();
  const out = { place: s, streetType: "", street: "", house: "", corp: "", room: "" };
  if (!s) return out;

  // Тип вулиці — перша згадка будь-якого з варіантів
  let idx = -1, type = "";
  for (const [abbr, full] of STREET_TYPES) {
    const re = new RegExp(`(^|[\\s,])(${abbr}\\.?|${full})([\\s,])`, "i");
    const m = s.match(re);
    if (m && (idx === -1 || m.index < idx)) { idx = m.index; type = full; }
  }
  if (idx === -1) return out;                    // адреса без вулиці — лишаємо як є

  out.place = s.slice(0, idx).replace(/[,\s]+$/, "");
  out.streetType = type;
  let rest = s.slice(idx).replace(/^[\s,]+/, "").replace(/^\S+[.\s]+/, "");

  // Будинок: «БУД. 1-А», «буд.1А», «1-А»
  const h = rest.match(/(?:буд\.?|будинок)\s*([0-9]+)\s*[-/]?\s*([А-ЯІЇЄA-Z]?)/i)
    ?? rest.match(/,\s*([0-9]+)\s*[-/]?\s*([А-ЯІЇЄA-Z]?)\s*$/i);
  if (h) {
    out.house = h[1];
    out.corp = (h[2] || "").trim();
    rest = rest.slice(0, h.index);
  }
  const r = rest.match(/(?:кв\.?|оф\.?|прим\.?)\s*([0-9А-Яа-яA-Za-z-]+)/i);
  if (r) { out.room = r[1]; rest = rest.slice(0, r.index); }

  out.street = rest.replace(/[,\s]+$/, "").replace(/^[,\s]+/, "");
  return out;
}

// Об'єкти з облікової картки платника — джерело правди про те, що вже на обліку
export function objectsFromCard(db) {
  const row = db.prepare(`SELECT json FROM jsonstore WHERE key='payer_card'`).get();
  if (!row) return [];
  let blocks = [];
  try { blocks = JSON.parse(row.json) || []; } catch { return []; }
  const b = blocks.find((x) => /об.єкти оподаткування/i.test(x.title || ""));
  return (b?.listValues || []).map((o) => {
    const id = String(o.TO_CODE ?? "");
    const a = parseAddress(o.ADDRESS);
    return {
      objectId: id,
      // Ідентифікатор = код типу + 5-значний номер, отже тип — усе, крім останніх п'яти цифр
      type: id.length > 5 ? id.slice(0, id.length - 5) : "",
      name: o.NAME || o.TYPE_OBJECT_NAME || "",
      typeName: o.TYPE_OBJECT_NAME || "",
      katottg: o.KATOTTG || "",
      address: o.ADDRESS || "",
      ...a,
      state: stateCode(o.STAN_OBJECT),
      stateText: o.STAN_OBJECT || "",
      right: rightCode(o.TYPE_OF_RIGHTS),
      rightText: o.TYPE_OF_RIGHTS || "",
      regNum: o.REG_NUM_OBJ || "",
      since: o.D_ACC_START || "",
      sti: o.C_DISTR || "",
    };
  });
}

// Наступний вільний внутрішній номер для типу об'єкта (5 цифр у межах типу)
export function nextObjectId(existing, type) {
  const t = String(type);
  const used = existing
    .filter((o) => o.objectId.length > 5 && o.objectId.slice(0, o.objectId.length - 5) === t)
    .map((o) => Number(o.objectId.slice(-5)));
  const next = used.length ? Math.max(...used) + 1 : 1;
  return `${t}${String(next).padStart(5, "0")}`;
}

// rows: [{ sign, type, name, objectId, katottg, place, streetType, street, house,
//          corp, room, extra, state, right, nonMain, regNum }]
export function build20oppXml({ org, rows, docCnt = 1, period, fillDate = new Date() }) {
  const [year, month] = period
    ? [Number(period.slice(0, 4)), Number(period.slice(5, 7))]
    : [fillDate.getFullYear(), fillDate.getMonth() + 1];
  const L = [];
  const put = (t, v) => L.push(`        <${t}>${esc(v)}</${t}>`);
  // Порожні графи у формі подаються явним nil, інакше ДПС лається на структуру
  const cell = (tag, n, v) => L.push(
    v === null || v === undefined || v === ""
      ? `        <${tag} ROWNUM="${n}" xsi:nil="true"/>`
      : `        <${tag} ROWNUM="${n}">${esc(v)}</${tag}>`);

  L.push(`<?xml version="1.0" encoding="windows-1251"?>`);
  L.push(`<DECLAR xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="J1312007.xsd">`);
  L.push(`    <DECLARHEAD>`);
  put("TIN", org.tin);
  put("C_DOC", "J13"); put("C_DOC_SUB", "120"); put("C_DOC_VER", "7");
  put("C_DOC_TYPE", 0); put("C_DOC_CNT", docCnt);
  put("C_REG", org.reg); put("C_RAJ", org.raj);
  put("PERIOD_MONTH", month); put("PERIOD_TYPE", 1); put("PERIOD_YEAR", year);
  put("C_STI_ORIG", org.sti); put("C_DOC_STAN", 1);
  L.push(`        <LINKED_DOCS xsi:nil="true"/>`);
  put("D_FILL", ddmmyyyy(fillDate));
  put("SOFTWARE", "eTax");
  L.push(`    </DECLARHEAD>`);
  L.push(`    <DECLARBODY>`);
  put("HSTI", org.stiName); put("HTIN", org.tin); put("HNAME", org.name);

  rows.forEach((r, i) => {
    const n = i + 1;
    cell("T1RXXXXG2", n, r.sign ?? 1);
    cell("T1RXXXXG3", n, r.type);
    cell("T1RXXXXG4S", n, r.name);
    cell("T1RXXXXG5", n, r.objectId);
    cell("T1RXXXXG6S", n, r.katottg);
    cell("T1RXXXXG7S", n, r.place);
    cell("T1RXXXXG8S", n, r.streetType);
    cell("T1RXXXXG9S", n, r.street);
    cell("T1RXXXXG10S", n, r.house);
    cell("T1RXXXXG11S", n, r.corp);
    cell("T1RXXXXG12S", n, r.room);
    cell("T1RXXXXG13S", n, r.extra);
    cell("T1RXXXXG14", n, r.state);
    cell("T1RXXXXG15", n, r.right);
    cell("T1RXXXXG16", n, r.nonMain ? 1 : null);
    cell("T1RXXXXG17S", n, r.regNum);
  });

  put("HBOS", org.bos);
  put("HFILL", ddmmyyyy(fillDate));
  L.push(`    </DECLARBODY>`);
  L.push(`</DECLAR>`);
  return L.join("\n");
}

// Перевірка рядків за правилами форми. Повертає масив зауважень;
// критичні починаються зі слова «Рядок» і блокують подачу.
export function validateObjects(rows) {
  const out = [];
  if (!rows.length) out.push("Немає жодного об'єкта — додайте рядок");
  const seen = new Set();
  rows.forEach((r, i) => {
    const n = i + 1;
    if (!INFO_SIGN[Number(r.sign)]) out.push(`Рядок ${n}: невідома ознака «${r.sign}» (1, 3 або 6)`);
    if (!r.type) out.push(`Рядок ${n}: не вказано тип об'єкта`);
    if (!r.name) out.push(`Рядок ${n}: не вказано найменування`);
    if (!/^\d{6,}$/.test(String(r.objectId ?? ""))) {
      out.push(`Рядок ${n}: ідентифікатор «${r.objectId ?? ""}» має бути код типу + 5 цифр`);
    } else if (r.type && !String(r.objectId).startsWith(String(r.type))) {
      out.push(`Рядок ${n}: ідентифікатор не починається з коду типу ${r.type}`);
    }
    if (seen.has(r.objectId)) out.push(`Рядок ${n}: ідентифікатор ${r.objectId} повторюється`);
    seen.add(r.objectId);
    if (!/^UA\d{17}$/.test(String(r.katottg ?? ""))) {
      out.push(`Рядок ${n}: КАТОТТГ «${r.katottg ?? ""}» має вигляд UA + 17 цифр`);
    }
    if (!OBJ_STATE[Number(r.state)]) out.push(`Рядок ${n}: невідомий стан об'єкта «${r.state}»`);
    if (!OBJ_RIGHT[Number(r.right)]) out.push(`Рядок ${n}: невідомий вид права «${r.right}»`);
    if (!r.place) out.push(`Рядок ${n}: не вказано населений пункт`);
    // Закриття об'єкта: стан має бути «відчужений/повернутий» або «непридатний»
    if (Number(r.sign) === 6 && ![5, 6, 7].includes(Number(r.state))) {
      out.push(`Увага, рядок ${n}: для закриття стан зазвичай 6 (відчужений/повернутий), а не ${r.state}`);
    }
  });
  return out;
}

// Строк подання — 10 робочих днів від дати події
export function deadline(from, days = 10) {
  const d = new Date(from);
  let left = days;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) left--;
  }
  return d;
}
